/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { msdyn365Commerce } from '@msdyn365-commerce/core';
import { Cart } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { action, observable } from 'mobx';

import { clearCartData, getEventId, getGlobantSuiteId, removeNewCartPosData } from './cookieutils';

/** Cart id per suite/event pair. The cart cookie names a single cart for the whole site. */
const CART_SCOPE_REGISTRY_KEY = 'cartsByScope';

/** The pair the cart cookie currently belongs to. */
const ACTIVE_CART_SCOPE_KEY = 'activeCartScope';

const RESUME_CART_ID_KEY = 'resumeCartId';

export const CART_SCOPE_SETTLE_TIMEOUT_MS = 30000;

export interface ICartScope {
    suiteId: string;
    eventId: string;
}

/**
 * keep - the active cart belongs to this pair.
 * restore - point the site at cartIdToRestore.
 * fresh - start a new cart for this pair.
 */
export type CartScopeAction = 'keep' | 'restore' | 'fresh';

export interface ICartScopeDecision {
    scopeKey: string;
    action: CartScopeAction;
    cartIdToRestore?: string;

    /** Whether this pair is not the one the cart cookie belonged to. True even when the action is keep. */
    isScopeChange: boolean;
}

export interface ICartScopeState {
    scope: ICartScope;
    activeCartId?: string;
    isActiveCartEmpty: boolean;
    isActiveCartFromAnotherScope: boolean;
}

interface ICartScopeSwitch {
    settle?: () => void;
    settled?: Promise<void>;
    isReloading?: boolean;
}

interface ICartScopeSwitchHolder {
    __lacCartScopeSwitch?: ICartScopeSwitch;
}

class CartScopeState {
    @observable public isSettled: boolean = false;

    @action
    public settle(): void {
        this.isSettled = true;
    }
}

/** Whether this page's cart has been confirmed for its customer and suite. False until then, on the server too. */
export const cartScopeState = new CartScopeState();

export const getCartScopeKey = (scope: ICartScope): string => `${scope.suiteId}|${scope.eventId}`;

/** Whether both halves of the pair are known. */
export const isCompleteCartScope = (scope: ICartScope): boolean => !!scope.suiteId && !!scope.eventId;

/**
 * The suite/event pair being opened. The query string wins over the cookies.
 *
 * A url that moves to another suite without naming the event leaves the event unknowable: the cookie holds
 * the event of the suite being left, and pairing the two would invent a pair the customer never opened. The
 * pair is reported incomplete instead. The reverse is safe, so an event on its own is honoured.
 */
export const readCartScope = (suiteIdFromUrl?: string | null, eventIdFromUrl?: string | null): ICartScope => {
    const suiteIdFromCookie = getGlobantSuiteId() || '';
    const isMovingSuite = !!suiteIdFromUrl && suiteIdFromUrl !== suiteIdFromCookie;

    return {
        suiteId: suiteIdFromUrl || suiteIdFromCookie,
        eventId: eventIdFromUrl || (isMovingSuite ? '' : getEventId() || '')
    };
};

const readRegistry = (): { [scopeKey: string]: string } => {
    if (!msdyn365Commerce.isBrowser) {
        return {};
    }

    try {
        const stored = localStorage.getItem(CART_SCOPE_REGISTRY_KEY);
        const parsed = stored ? JSON.parse(stored) : undefined;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
        console.log('LTS Info', 'cartscope: could not read the cart registry', error);
        return {};
    }
};

const writeRegistry = (registry: { [scopeKey: string]: string }): boolean => {
    if (!msdyn365Commerce.isBrowser) {
        return false;
    }

    try {
        localStorage.setItem(CART_SCOPE_REGISTRY_KEY, JSON.stringify(registry));
        return true;
    } catch (error) {
        console.log('LTS Info', 'cartscope: could not write the cart registry', error);
        return false;
    }
};

/**
 * Whether what is decided here can be recorded. Storage that cannot be written would have every load read as
 * the same switch, and each one would move the site onto another cart and reload, so a switch is only started
 * once it can be remembered.
 */
const isCartScopeStorageWritable = (): boolean => writeRegistry(readRegistry());

/**
 * The pair the cart cookie belongs to, and whether that could be established at all. Storage that throws is
 * reported as unreadable rather than as an absent pair; the two lead to opposite decisions.
 */
const readActiveScope = (): { isReadable: boolean; scopeKey?: string } => {
    if (!msdyn365Commerce.isBrowser) {
        return { isReadable: false };
    }

    try {
        return { isReadable: true, scopeKey: localStorage.getItem(ACTIVE_CART_SCOPE_KEY) || undefined };
    } catch (error) {
        console.log('LTS Info', 'cartscope: could not read the active scope', error);
        return { isReadable: false };
    }
};

const writeActiveScopeKey = (scopeKey: string): boolean => {
    if (!msdyn365Commerce.isBrowser) {
        return false;
    }

    try {
        localStorage.setItem(ACTIVE_CART_SCOPE_KEY, scopeKey);
        return true;
    } catch (error) {
        console.log('LTS Info', 'cartscope: could not write the active scope', error);
        return false;
    }
};

const rememberCartForScope = (scopeKey: string, cartId?: string): void => {
    if (!scopeKey || !cartId) {
        return;
    }

    const registry = readRegistry();
    if (registry[scopeKey] === cartId) {
        return;
    }

    registry[scopeKey] = cartId;
    writeRegistry(registry);
};

/** Drops the line-level mirrors of the active cart, which would otherwise reach the next checkout payload. */
export const clearActiveCartMirrors = (): void => {
    removeNewCartPosData();
    clearCartData();

    if (!msdyn365Commerce.isBrowser) {
        return;
    }

    try {
        localStorage.removeItem(RESUME_CART_ID_KEY);
    } catch (error) {
        console.log('LTS Info', 'cartscope: could not clear the resumed cart id', error);
    }
};

/**
 * Whether a cart was built under a different event than the one resolved for this page. Only the event is
 * judged: LS_TableAssigned cannot be compared against at the point the cart is settled. An empty cart, or one
 * carrying no event stamp, is left alone.
 */
export const isCartFromAnotherScope = (cart: Cart | undefined, eventId: string): boolean => {
    if (!cart?.CartLines?.length || !eventId) {
        return false;
    }

    const cartEventId = cart?.ExtensionProperties?.find(p => p.Key === 'ITS_TableEventId')?.Value?.StringValue;

    return !!cartEventId && cartEventId !== eventId;
};

/**
 * Settles which cart the pair being opened should be on, and parks the outgoing cart under its own pair.
 * commitCartScope records the incoming pair once the caller has the cart in hand.
 */
export const resolveCartScope = (state: ICartScopeState): ICartScopeDecision => {
    const scopeKey = getCartScopeKey(state.scope);
    const activeScope = readActiveScope();
    const previousScopeKey = activeScope.isReadable ? activeScope.scopeKey : undefined;

    // A switch that cannot be recorded would be recognised again on the load after it, and on every load after.
    if (!previousScopeKey || previousScopeKey === scopeKey || !isCartScopeStorageWritable()) {
        return {
            scopeKey,
            action: state.isActiveCartFromAnotherScope ? 'fresh' : 'keep',
            isScopeChange: !!previousScopeKey && previousScopeKey !== scopeKey
        };
    }

    rememberCartForScope(previousScopeKey, state.activeCartId);

    const cartIdToRestore = readRegistry()[scopeKey];
    if (cartIdToRestore && cartIdToRestore !== state.activeCartId) {
        return { scopeKey, action: 'restore', cartIdToRestore, isScopeChange: true };
    }

    // An empty cart carries nothing over, so the pair being opened can take it on as it stands.
    if (state.isActiveCartEmpty && !state.isActiveCartFromAnotherScope) {
        return { scopeKey, action: 'keep', isScopeChange: true };
    }

    return { scopeKey, action: 'fresh', isScopeChange: true };
};

/**
 * Settles which cart a suite opened without naming its event should be on. The event cannot be known, so no
 * held cart is restored; the outgoing pair's cart is parked under that pair and the suite starts on its own.
 * Undefined where the suite is not changing or the move cannot be recorded.
 */
export const resolveSuiteChangeWithoutEvent = (state: ICartScopeState): ICartScopeDecision | undefined => {
    const activeScope = readActiveScope();
    const previousScopeKey = activeScope.isReadable ? activeScope.scopeKey : undefined;

    if (!previousScopeKey || !state.scope.suiteId || state.scope.eventId) {
        return undefined;
    }

    if (previousScopeKey.split('|')[0] === state.scope.suiteId || !isCartScopeStorageWritable()) {
        return undefined;
    }

    rememberCartForScope(previousScopeKey, state.activeCartId);

    return { scopeKey: getCartScopeKey(state.scope), action: state.isActiveCartEmpty ? 'keep' : 'fresh', isScopeChange: true };
};

export interface ICartScopeSettlement {
    decision?: ICartScopeDecision;

    /** Whether the site was to stay on its cart and the server says that cart is another customer's. */
    isActiveCartAnothers: boolean;
}

/** Whether a decision leaves the site on the cart it is already on. */
export const staysOnActiveCart = (decision?: ICartScopeDecision): boolean => !decision || decision.action === 'keep';

/** Decides the cart for a pair, then asks whose the active cart is only where the site stays on it. */
export const settleCartScopeDecision = async (
    state: ICartScopeState,
    isActiveCartAnothers: (cartId: string) => Promise<boolean>
): Promise<ICartScopeSettlement> => {
    const decision = isCompleteCartScope(state.scope) ? resolveCartScope(state) : resolveSuiteChangeWithoutEvent(state);

    if (!state.activeCartId || !staysOnActiveCart(decision)) {
        return { decision, isActiveCartAnothers: false };
    }

    return { decision, isActiveCartAnothers: await isActiveCartAnothers(state.activeCartId) };
};

/** Takes a cart off every pair it is held for, so it is never restored again. */
export const forgetCart = (cartId?: string): void => {
    if (!cartId) {
        return;
    }

    const registry = readRegistry();
    const keys = Object.keys(registry).filter(key => registry[key] === cartId);
    if (keys.length === 0) {
        return;
    }

    keys.forEach(key => delete registry[key]);
    writeRegistry(registry);
};

/**
 * Records the cart the pair ended up on and makes that pair the active one. A cart id is held against one
 * pair only, so committing it here takes it off any other pair.
 */
export const commitCartScope = (scopeKey: string, cartId?: string): void => {
    if (!scopeKey) {
        return;
    }

    const registry = readRegistry();

    if (cartId) {
        for (const key of Object.keys(registry)) {
            if (key !== scopeKey && registry[key] === cartId) {
                delete registry[key];
            }
        }

        registry[scopeKey] = cartId;
    } else {
        delete registry[scopeKey];
    }

    writeRegistry(registry);

    writeActiveScopeKey(scopeKey);
};

const getCartScopeSwitch = (): ICartScopeSwitch | undefined => {
    if (!msdyn365Commerce.isBrowser) {
        return undefined;
    }

    return ((window as unknown) as ICartScopeSwitchHolder).__lacCartScopeSwitch;
};

/**
 * Marks the cart as unsettled while this pair's cart is worked out. Called synchronously, before that pair
 * reaches the cookies the rest of the page reads, so work keyed on those cookies waits.
 */
export const beginCartScopeWork = (): void => {
    if (!msdyn365Commerce.isBrowser || getCartScopeSwitch()) {
        return;
    }

    const pending: ICartScopeSwitch = {};
    pending.settled = new Promise<void>(resolve => {
        pending.settle = resolve;
    });

    ((window as unknown) as ICartScopeSwitchHolder).__lacCartScopeSwitch = pending;
};

export const endCartScopeWork = (): void => {
    cartScopeState.settle();

    const pending = getCartScopeSwitch();
    if (!pending) {
        return;
    }

    ((window as unknown) as ICartScopeSwitchHolder).__lacCartScopeSwitch = undefined;
    pending.settle?.();
};

/** Holds the gate up for good, because the page is being replaced. */
export const markCartScopeReloading = (): void => {
    const pending = getCartScopeSwitch();
    if (pending) {
        pending.isReloading = true;
    }
};

/**
 * Whether the cart settled: true once nothing is working on it, false where a bounded wait ran out with the switch
 * still under way. Never resolves while a reload is pending.
 */
export const whenCartScopeSettled = async (): Promise<boolean> => {
    const pending = getCartScopeSwitch();
    if (!pending?.settled) {
        return true;
    }

    const givingUp = new Promise<boolean>(resolve => {
        window.setTimeout(() => {
            if (!pending.isReloading) {
                resolve(false);
            }
        }, CART_SCOPE_SETTLE_TIMEOUT_MS);
    });

    return Promise.race([pending.settled.then(() => true), givingUp]);
};

/** Runs work once the cart has settled; skips it where the switch is still under way when the wait runs out. */
export const runOnceCartScopeSettled = async (work: () => Promise<void>): Promise<boolean> => {
    if (!(await whenCartScopeSettled())) {
        return false;
    }

    await work();
    return true;
};
